import { useEffect, useState } from 'react'
import { Bell, BellRing, BellOff, Square, Trash2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  isPushSupported,
  getPushStatus,
  enablePush,
  disablePush,
  refreshPushLanguage,
  type PushStatus,
} from '@/services/pushService'
import {
  MAX_ALERT_ZONES,
  zoneSizeKm,
  type AlertZone,
} from '@/services/alertZones'

interface NotificationToggleProps {
  /** Areas the user drew; empty means they hear about every new marker. */
  zones: AlertZone[]
  /** Arm rectangle drawing on the map. */
  onDrawZone: () => void
  onRemoveZone: (id: string) => void
  /** Move the map onto a zone, so a list row points at something visible. */
  onFocusZone: (zone: AlertZone) => void
}

/**
 * Round control (matching the map buttons) to turn "notify me when someone
 * else adds a marker" on or off. Hidden entirely on browsers without push
 * support; shown disabled if the user has blocked notifications.
 *
 * Once notifications are on it opens a panel for the alert zones — the
 * rectangles that narrow those notifications down to chosen parts of the map.
 */
export function NotificationToggle({
  zones,
  onDrawZone,
  onRemoveZone,
  onFocusZone,
}: NotificationToggleProps) {
  const { t, i18n } = useTranslation()
  const language = (i18n.language || 'en').split('-')[0]
  const [status, setStatus] = useState<PushStatus>({
    supported: isPushSupported(),
    permission: 'default',
    subscribed: false,
  })
  const [busy, setBusy] = useState(false)
  const [showPanel, setShowPanel] = useState(false)

  // Reflect the real subscription state once, on mount.
  useEffect(() => {
    void getPushStatus().then(setStatus)
  }, [])

  // Keep the server's copy of our language in step with the UI so notifications
  // arrive translated.
  useEffect(() => {
    if (status.subscribed) {
      void refreshPushLanguage(i18n.language)
    }
  }, [i18n.language, status.subscribed])

  if (!status.supported) return null

  const blocked = status.permission === 'denied'

  const handleClick = async () => {
    if (busy || blocked) return
    if (status.subscribed) {
      // Already on: the bell is the way into the zone settings, which is also
      // where turning it back off lives.
      setShowPanel((open) => !open)
      return
    }

    setBusy(true)
    try {
      const next = await enablePush(i18n.language)
      setStatus(next)
      // Offer the zones straight away — it's the first thing worth deciding
      // once notifications are on.
      if (next.subscribed) setShowPanel(true)
    } catch (error) {
      console.error('Failed to enable notifications:', error)
      setStatus(await getPushStatus())
    } finally {
      setBusy(false)
    }
  }

  const handleDisable = async () => {
    if (busy) return
    setBusy(true)
    try {
      await disablePush()
      setStatus((s) => ({ ...s, subscribed: false }))
      setShowPanel(false)
    } catch (error) {
      console.error('Failed to disable notifications:', error)
      setStatus(await getPushStatus())
    } finally {
      setBusy(false)
    }
  }

  const formatSize = (zone: AlertZone) => {
    const { width, height } = zoneSizeKm(zone)
    const format = (km: number) =>
      km.toLocaleString(language, { maximumFractionDigits: 1 })
    return `${format(width)} × ${format(height)} km`
  }

  let label = t('notifications.enable')
  let color = 'bg-slate-600 hover:bg-slate-700'
  let Icon = Bell
  if (blocked) {
    label = t('notifications.blocked')
    color = 'bg-gray-400 cursor-not-allowed'
    Icon = BellOff
  } else if (status.subscribed) {
    label = t('zones.title')
    color = 'bg-amber-500 hover:bg-amber-600'
    Icon = BellRing
  }

  return (
    <div className="relative">
      {showPanel && (
        <div
          // Positioned by the LTR control column, but its own text needs the
          // reading direction of the interface.
          dir={language === 'ar' ? 'rtl' : 'ltr'}
          // The max-width keeps it on screen next to the button column on the
          // narrowest phones, where 16rem would overflow the left edge.
          className="absolute bottom-0 right-full mr-2 w-64 max-w-[calc(100vw-6rem)] max-h-[60vh] overflow-y-auto bg-white rounded-lg shadow-xl p-4"
        >
          <div className="flex items-center justify-between gap-2 mb-2">
            <h3 className="font-semibold text-gray-900">{t('zones.title')}</h3>
            <button
              type="button"
              onClick={() => setShowPanel(false)}
              className="text-gray-400 hover:text-gray-600"
              aria-label={t('common.close')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <p className="text-sm text-gray-500 mb-3">
            {zones.length ? t('zones.restricted') : t('zones.everywhere')}
          </p>

          {zones.length > 0 && (
            <ul className="mb-3 flex flex-col gap-1">
              {zones.map((zone, index) => (
                <li key={zone.id} className="flex items-center gap-2">
                  {/* min-w-0 lets the label truncate instead of shoving the
                      delete button off the edge on a narrow screen. */}
                  <button
                    type="button"
                    onClick={() => onFocusZone(zone)}
                    className="flex-1 min-w-0 flex items-center gap-2 text-start text-sm text-gray-700 hover:bg-gray-100 rounded px-2 py-1"
                  >
                    <Square className="w-4 h-4 shrink-0 text-amber-500 fill-amber-100" />
                    <span className="truncate">
                      {t('zones.areaLabel', { index: index + 1 })}
                      {/* Kept LTR so "0.9 × 1.1 km" doesn't get reordered into
                          "km 1.1 × 0.9" inside the Arabic interface. */}
                      <span className="text-gray-400" dir="ltr">
                        {' '}
                        · {formatSize(zone)}
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => onRemoveZone(zone.id)}
                    className="shrink-0 text-gray-400 hover:text-red-600 p-1"
                    aria-label={t('zones.remove')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            onClick={() => {
              setShowPanel(false)
              onDrawZone()
            }}
            disabled={zones.length >= MAX_ALERT_ZONES}
            className="w-full bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white text-sm py-2 px-3 rounded-lg transition-colors"
          >
            {t('zones.draw')}
          </button>

          <button
            type="button"
            onClick={handleDisable}
            disabled={busy}
            className="w-full mt-2 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 py-1"
          >
            {t('notifications.disable')}
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={handleClick}
        disabled={busy || blocked}
        className={`${color} disabled:opacity-70 text-white shadow-lg rounded-full p-4 transition-colors`}
        aria-label={label}
        title={label}
      >
        {busy ? (
          <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <Icon className="w-6 h-6" />
        )}
      </button>
    </div>
  )
}
